import React from 'react';
import { Alert, Box, Link, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import InfluenceHeadlineCards from './InfluenceHeadlineCards';
import { readHeadline, hasPooled, fmtPts, fmtInterval } from '../../utils/influenceScorecard';
import useInfluenceScorecard from '../../hooks/useInfluenceScorecard';

const ALL = { window: 'since' };

// The new scorecard headline next to the old tiles (cutover off) or in their place (cutover on).
// `cutover` is null while the page has not yet heard whether the influence view is on: render nothing then, or a refresh briefly flashes
// the "older comparison group" warning meant for the cutover-off state.
export default function InfluenceSummaryStrip({ cutover }) {
  const { loading, data } = useInfluenceScorecard(ALL);
  if (loading || cutover === null) return null;
  if (!data?.available) {
    return cutover ? (
      <Alert severity="info" sx={{ mb: 2 }} aria-label="Influence scorecard unavailable">The TEC influence scorecard has no data loaded yet. {data?.reason}</Alert>
    ) : null;
  }
  return (
    <Box sx={{ mb: 3 }} aria-label="TEC influence scorecard summary">
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>TEC influence scorecard (baseline corrected)</Typography>
        <Link component={RouterLink} to="/exec/influence" underline="hover">Open the full scorecard</Link>
      </Box>
      {!cutover && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          The tiles above use the older comparison group, in which roughly {Math.round(data.meta?.baseline_correction?.old_baseline_share_with_attending_se_pct || 42)}% of &quot;baseline&quot; deals
          actually had an SE who attended TEC. The numbers below correct that, so the two will differ.
        </Alert>
      )}
      <InfluenceHeadlineCards headline={data.headline} horizon={180} showDetails={false} />
      {hasPooled(data.headline) && (
        <Typography variant="caption" color="text.secondary" aria-label="Other horizons" sx={{ display: 'block', mt: 1 }}>
          {[90, 365].map((h) => {
            const r = readHeadline(data.headline, 'pooled', h);
            return r ? `${h}-day: ${r.lift == null ? 'n/a' : fmtPts(r.lift)} ${fmtInterval(r.lo, r.hi)}` : null;
          }).filter(Boolean).join(' · ')}
          {' '}(all influenced deals; the 180-day number is the pre-set headline, and longer horizons are lower)
        </Typography>
      )}
    </Box>
  );
}
